import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import ConfirmDialog from '../ConfirmDialog';
import { Upload, Trash2, Eye, Edit3, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { docHasPremium } from '../../lib/docTypes';
import { API } from './utils';

export default function DocumentDetail({
  typeValue, typeLabel, icon: Icon, doc, status, days, formatDate,
  propertyId, onUpload, onReplace, onUpdate, onDelete, onChanged, uploading, analyzing,
}) {
  const [file, setFile] = useState(null);
  const [expiryDate, setExpiryDate] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editName, setEditName] = useState('');
  const [editExpiry, setEditExpiry] = useState('');
  const [editPremium, setEditPremium] = useState('');
  const [editMonthlyPremium, setEditMonthlyPremium] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const showPremium = docHasPremium(typeValue, typeLabel);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) setFile(selectedFile);
  };

  const hasFile = !!doc?.cloudinary_url;

  const handleUpload = () => {
    if (!file) return;
    if (doc && hasFile) {
      // Replace flow: AI re-analyses the new file just like a fresh upload
      onReplace(typeValue, file, null, doc.id);
    } else {
      // First upload — either no record yet, or an expiry-only record waiting
      // for its file. Backend's POST /documents auto-upgrades the existing
      // fileless record in the second case, so onUpload is correct for both.
      onUpload(typeValue, file, null);
    }
    setFile(null);
    setExpiryDate('');
    setShowUpload(false);
  };

  const openEdit = () => {
    setEditName(doc?.name || '');
    setEditExpiry(doc?.expiry_date || '');
    setEditPremium(doc?.premium_amount != null ? String(doc.premium_amount) : '');
    setEditMonthlyPremium(doc?.monthly_premium_amount != null ? String(doc.monthly_premium_amount) : '');
    setShowEdit(true);
  };

  const saveEdit = async () => {
    if (!editExpiry) {
      toast.error('Expiry date is required');
      return;
    }
    const payload = {
      name: editName.trim() || null,
      expiry_date: editExpiry,
    };
    if (showPremium) {
      const annual = editPremium.trim();
      const monthly = editMonthlyPremium.trim();
      payload.premium_amount = annual === '' ? null : parseFloat(annual);
      payload.monthly_premium_amount = monthly === '' ? null : parseFloat(monthly);
      if ((annual !== '' && isNaN(payload.premium_amount)) ||
          (monthly !== '' && isNaN(payload.monthly_premium_amount))) {
        toast.error('Premium values must be numbers');
        return;
      }
    }
    await onUpdate(doc.id, payload);
    setShowEdit(false);
  };

  // Internal helper that fetches the document via the auth-protected proxy
  // and returns a same-origin blob URL the browser can open without any
  // cross-origin or popup-blocker issues.
  const fetchDocumentBlobUrl = async (download = false) => {
    const token = localStorage.getItem('token');
    const url = `${API}/documents/${doc.id}/file${download ? '?download=true' : ''}`;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });
    const ct = response.headers?.['content-type'] || response.data?.type || 'application/octet-stream';
    const blob = response.data instanceof Blob
      ? response.data
      : new Blob([response.data], { type: ct });
    const blobUrl = URL.createObjectURL(blob);
    return { blobUrl, contentType: ct, headers: response.headers || {} };
  };

  const handleViewDocument = async () => {
    if (!doc) return;
    try {
      const { blobUrl } = await fetchDocumentBlobUrl(false);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => {
        try { URL.revokeObjectURL(blobUrl); } catch (_) { /* noop */ }
      }, 60000);
    } catch (err) {
      console.error('View document failed:', err);
      const status = err?.response?.status;
      toast.error(
        status === 401 || status === 403
          ? 'Session expired — please sign in again'
          : 'Unable to open document. Please try again.'
      );
    }
  };

  const handleDownloadDocument = async () => {
    if (!doc) return;
    try {
      const { blobUrl, headers } = await fetchDocumentBlobUrl(true);
      const disposition = headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : `${typeValue}_${doc.id.slice(0, 8)}`;
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => {
        try { URL.revokeObjectURL(blobUrl); } catch (_) { /* noop */ }
      }, 60000);
    } catch (err) {
      console.error('Download document failed:', err);
      const status = err?.response?.status;
      toast.error(
        status === 401 || status === 403
          ? 'Session expired — please sign in again'
          : 'Unable to download document. Please try again.'
      );
    }
  };

  return (
    <div className="document-detail-panel">
      <div className="document-detail-header">
        <div className="doc-detail-icon">
          <Icon size={32} />
        </div>
        <div>
          <h3 className="doc-detail-title">{(doc && doc.name) ? doc.name : typeLabel}</h3>
          {doc && doc.name && (
            <div className="doc-detail-subtitle">{typeLabel}</div>
          )}
          {doc && (
            <div className={`doc-detail-status status-${status}`}>
              {days === null ? (
                'No expiry set — click Edit to add'
              ) : (
                <>
                  {status === 'urgent' && '🔴 Urgent - '}
                  {status === 'warning' && '⚠️ Warning - '}
                  {days < 0 ? `Expired ${Math.abs(days)} days ago` : `Expires in ${days} days`}
                </>
              )}
            </div>
          )}
          {!doc && (
            <div className="doc-detail-status missing">No document uploaded</div>
          )}
        </div>
      </div>

      {doc ? (
        <div className="document-detail-content">
          {analyzing && (
            <div className="ai-analyzing-banner" data-testid={`analyzing-banner-${typeValue}`}>
              <span className="ai-spinner" /> Reading document with AI…
            </div>
          )}
          <div className="doc-detail-info-grid">
            <div className="doc-detail-info-item">
              <div className="doc-detail-label">Expiry Date</div>
              <div className="doc-detail-value">{formatDate(doc.expiry_date)}</div>
            </div>
            <div className="doc-detail-info-item">
              <div className="doc-detail-label">Days Remaining</div>
              <div className={`doc-detail-value status-${status}`}>{days === null ? '—' : `${days} days`}</div>
            </div>
            <div className="doc-detail-info-item">
              <div className="doc-detail-label">Uploaded On</div>
              <div className="doc-detail-value">{formatDate(doc.uploaded_at)}</div>
            </div>
            {showPremium && (
              <>
                <div className="doc-detail-info-item">
                  <div className="doc-detail-label">Monthly Premium</div>
                  <div className="doc-detail-value">
                    {doc.monthly_premium_amount != null ? `£${Number(doc.monthly_premium_amount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                  </div>
                </div>
                <div className="doc-detail-info-item">
                  <div className="doc-detail-label">Annual Premium</div>
                  <div className="doc-detail-value">
                    {doc.premium_amount != null ? `£${Number(doc.premium_amount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                  </div>
                </div>
              </>
            )}
          </div>

          {showEdit ? (
            <div className="upload-form-detail" data-testid={`edit-form-${typeValue}`}>
              <div className="form-group">
                <Label htmlFor={`edit-name-${typeValue}`}>Document Name (optional)</Label>
                <Input
                  id={`edit-name-${typeValue}`}
                  type="text"
                  value={editName}
                  placeholder={typeLabel}
                  onChange={(e) => setEditName(e.target.value)}
                  data-testid={`edit-name-input-${typeValue}`}
                />
                <p className="file-hint">Leave blank to use default name "{typeLabel}".</p>
              </div>
              <div className="form-group">
                <Label htmlFor={`edit-expiry-${typeValue}`}>Expiry Date</Label>
                <Input
                  id={`edit-expiry-${typeValue}`}
                  type="date"
                  value={editExpiry}
                  onChange={(e) => setEditExpiry(e.target.value)}
                  required
                  data-testid={`edit-expiry-input-${typeValue}`}
                />
              </div>
              {showPremium && (
                <>
                  <div className="form-group">
                    <Label htmlFor={`edit-monthly-premium-${typeValue}`}>Monthly Premium (£)</Label>
                    <Input
                      id={`edit-monthly-premium-${typeValue}`}
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="e.g. 24.50"
                      value={editMonthlyPremium}
                      onChange={(e) => setEditMonthlyPremium(e.target.value)}
                      data-testid={`edit-monthly-premium-input-${typeValue}`}
                    />
                    <p className="file-hint">Monthly direct debit / instalment amount.</p>
                  </div>
                  <div className="form-group">
                    <Label htmlFor={`edit-premium-${typeValue}`}>Annual Premium (£)</Label>
                    <Input
                      id={`edit-premium-${typeValue}`}
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="e.g. 1450.50"
                      value={editPremium}
                      onChange={(e) => setEditPremium(e.target.value)}
                      data-testid={`edit-premium-input-${typeValue}`}
                    />
                    <p className="file-hint">Total annual cost. Use the higher of "annual lump sum" and "total cost paid monthly" if both are shown.</p>
                  </div>
                </>
              )}
              <div className="upload-actions-detail">
                <Button onClick={saveEdit} className="btn-primary" data-testid={`submit-edit-${typeValue}`}>
                  Save Changes
                </Button>
                <Button onClick={() => setShowEdit(false)} className="btn-secondary">Cancel</Button>
              </div>
            </div>
          ) : showUpload ? (
            <div className="upload-form-detail">
              <div className="form-group">
                <Label htmlFor={`file-replace-${typeValue}`}>Select Replacement File (PDF or Image)</Label>
                <Input
                  id={`file-replace-${typeValue}`}
                  type="file"
                  onChange={handleFileChange}
                  accept=".pdf,.jpg,.jpeg,.png"
                  data-testid={`replace-file-input-${typeValue}`}
                />
                <p className="file-hint">PDF, JPG or PNG. {hasFile ? 'The current file will be removed and AI will re-extract the document details.' : 'AI will read the document and confirm the expiry date.'}</p>
              </div>
              <div className="upload-actions-detail">
                <Button
                  onClick={handleUpload}
                  className="btn-primary"
                  disabled={!file || uploading}
                  data-testid={`submit-replace-${typeValue}`}
                >
                  {uploading ? 'Uploading...' : (hasFile ? 'Replace Document' : 'Upload Document')}
                </Button>
                <Button
                  onClick={() => { setShowUpload(false); setFile(null); setExpiryDate(''); }}
                  className="btn-secondary"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="doc-detail-actions">
              {hasFile && (
                <>
                  <Button
                    onClick={handleViewDocument}
                    className="btn-primary btn-large"
                    data-testid={`view-document-${typeValue}`}
                  >
                    <Eye size={20} />
                    View Document
                  </Button>
                  <Button
                    onClick={handleDownloadDocument}
                    className="btn-secondary"
                    data-testid={`download-document-${typeValue}`}
                  >
                    <Upload size={18} style={{ transform: 'rotate(180deg)' }} />
                    Download
                  </Button>
                  <Button
                    onClick={() => setShowUpload(true)}
                    className="btn-secondary"
                    data-testid={`replace-document-${typeValue}`}
                  >
                    <Upload size={18} />
                    Replace
                  </Button>
                </>
              )}
              {!hasFile && (
                <Button
                  onClick={() => setShowUpload(true)}
                  className="btn-primary btn-large"
                  data-testid={`upload-document-${typeValue}`}
                >
                  <Upload size={20} />
                  Upload Document
                </Button>
              )}
              <Button
                onClick={openEdit}
                className="btn-secondary"
                data-testid={`edit-document-${typeValue}`}
              >
                <Edit3 size={18} />
                Edit
              </Button>
              <Button
                onClick={() => setDeleteDialogOpen(true)}
                className="btn-danger"
                data-testid={`delete-document-${typeValue}`}
              >
                <Trash2 size={18} />
                Delete
              </Button>
            </div>
          )}

          <ConfirmDialog
            open={deleteDialogOpen && typeValue !== 'tenancy_agreement'}
            onOpenChange={setDeleteDialogOpen}
            title="Delete Document"
            description={`Are you sure you want to delete this ${(doc && doc.name) ? doc.name : typeLabel}? This action cannot be undone.`}
            onConfirm={() => onDelete(doc.id)}
            confirmText="Delete"
          />

          {/* Tenancy Agreement deletion: ask whether to keep or clear tenant
              details that came from this AST. */}
          <Dialog
            open={deleteDialogOpen && typeValue === 'tenancy_agreement'}
            onOpenChange={setDeleteDialogOpen}
          >
            <DialogContent className="confirm-dialog" data-testid="delete-tenancy-dialog">
              <DialogHeader>
                <div className="confirm-dialog-icon">
                  <AlertTriangle size={48} />
                </div>
                <DialogTitle className="confirm-dialog-title">Delete Tenancy Agreement?</DialogTitle>
                <DialogDescription className="confirm-dialog-description">
                  Do you want to keep the current tenant details (name, rent, dates, deposit) on file,
                  or clear them along with the agreement?
                </DialogDescription>
              </DialogHeader>
              <div className="confirm-dialog-actions" style={{ flexWrap: 'wrap', gap: 8 }}>
                <Button
                  onClick={() => setDeleteDialogOpen(false)}
                  className="btn-secondary"
                  data-testid="delete-tenancy-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    setDeleteDialogOpen(false);
                    await onDelete(doc.id);
                  }}
                  className="btn-primary"
                  data-testid="delete-tenancy-keep"
                >
                  Keep tenant details
                </Button>
                <Button
                  onClick={async () => {
                    setDeleteDialogOpen(false);
                    try {
                      const token = localStorage.getItem('token');
                      await axios.post(
                        `${API}/properties/${propertyId}/clear-tenant`,
                        {},
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      await onDelete(doc.id);
                      if (onChanged) await onChanged();
                      toast.success('Tenancy agreement deleted and tenant details cleared');
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Failed to clear tenant details');
                    }
                  }}
                  className="btn-danger"
                  data-testid="delete-tenancy-clear"
                >
                  Clear tenant details
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      ) : (
        <div className="document-upload-section">
          {!showUpload ? (
            <>
              <p className="upload-prompt">No document has been uploaded yet.</p>
              <Button
                onClick={() => setShowUpload(true)}
                className="btn-primary btn-large"
                data-testid={`upload-button-${typeValue}`}
              >
                <Upload size={20} />
                Upload {typeLabel}
              </Button>
            </>
          ) : (
            <div className="upload-form-detail">
              <div className="form-group">
                <Label htmlFor={`file-${typeValue}`}>Select File (PDF or Image)</Label>
                <Input
                  id={`file-${typeValue}`}
                  type="file"
                  onChange={handleFileChange}
                  accept=".pdf,.jpg,.jpeg,.png"
                  data-testid={`file-input-${typeValue}`}
                />
                <p className="file-hint">PDF, JPG or PNG. Once uploaded, AI will extract the expiry date and other details automatically.</p>
              </div>
              <div className="upload-actions-detail">
                <Button
                  onClick={handleUpload}
                  className="btn-primary"
                  disabled={!file || uploading}
                  data-testid={`submit-upload-${typeValue}`}
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </Button>
                <Button
                  onClick={() => {
                    setShowUpload(false);
                    setFile(null);
                    setExpiryDate('');
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
